/**
 * Room rows: public listing availability vs raw `available` + the availability date.
 *
 * `rooms.available_from` is the single source of truth for that date, and it is
 * DERIVED: the earliest confirmed move-out date on the room — a booking in
 * `move_out_pending` carrying a `move_out_date` — or NULL when no move-out has
 * been confirmed (migration `room_available_from_confirmed_moveout`). Nothing
 * else may write it, so a date on the listing always means a real tenant with a
 * real agreed leaving date.
 *
 * A booking whose tenant has left but whose deposit is not settled yet stays in
 * `move_out_pending`, and a new tenant can already have moved in. Such a
 * predecessor is ignored once a LATER tenancy has taken the room (deposit paid):
 * the latest tenant's move-out date — or none — is what the room carries
 * (migration `room_available_from_latest_tenant_wins`, COL-557). A past date
 * therefore only survives when nobody has taken the room since, and still means
 * "free now".
 *
 * It used to be written at MOVE-IN from the contract end date, which is how a
 * room whose tenant had just paid their deposit ended up publicly advertising
 * "Verfügbar ab 2. Okt" with nobody having given notice.
 *
 * `next_available_date` is the legacy mirror of the same value, kept only so
 * already deployed/cached frontend bundles keep reading a correct date.
 *
 * A CONFIRMED move-out date is the only date the public site ever shows. An
 * occupied room without one reads "Unavailable" on the listing — the sitting
 * contract's end (`rooms.earliest_move_out_date`) is not a vacancy: contracts
 * auto-prolong, so advertising it promised dates nobody had agreed to. The
 * home page goes further and shows only rooms that are free right now.
 */

export type RoomAvailabilityInput = {
  available?: boolean | null;
  /** Derived availability date (`rooms.available_from`). Authoritative. */
  available_from?: string | null;
  /** @deprecated Mirror of `available_from`; fallback for un-backfilled rows. */
  next_available_date?: string | null;
};

/**
 * Parse a date-only value as LOCAL midnight.
 *
 * `new Date("2026-11-01")` is parsed as UTC midnight, so reading local
 * components off it returns the previous day anywhere west of UTC. Every
 * reader of an availability date needs this, not just the ones in here.
 */
export function parseDateOnly(iso: string): Date {
  const part = iso.split("T")[0] ?? iso;
  const [y, m, d] = part.split("-").map((x) => Number(x));
  if (!y || !m || !d) return new Date(NaN);
  return new Date(y, m - 1, d);
}

function startOfTodayLocal(): Date {
  const x = new Date();
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
}

/**
 * The one availability date the public site labels a room with: the derived
 * `available_from`, falling back to the legacy mirror only when it is unset.
 */
export function resolveAvailableFrom(
  room: RoomAvailabilityInput
): string | null {
  return (
    room.available_from?.trim() || room.next_available_date?.trim() || null
  );
}

/**
 * Effective listing availability: if the availability date is in the future, the room is not bookable
 * until that date; once that day is reached (or passed), the room is shown as available again.
 */
export function getListingAvailability(room: RoomAvailabilityInput): {
  listingAvailable: boolean;
  nextAvailableDate: string | null;
} {
  const raw = room.available !== false;
  const nd = resolveAvailableFrom(room);
  if (nd) {
    const d = parseDateOnly(nd);
    const today = startOfTodayLocal();
    if (d > today) {
      return { listingAvailable: false, nextAvailableDate: nd };
    }
    return { listingAvailable: true, nextAvailableDate: null };
  }
  return { listingAvailable: raw, nextAvailableDate: null };
}

/**
 * Whether the public room page may offer "Apply": the room is free today, or it
 * carries a confirmed move-out date to apply against. Both mean the applicant
 * can name a move-in day that works; the booking form greys out every day before
 * the date and re-checks it on submit (`moveInAllowsBooking`).
 *
 * A taken room without a confirmed date stays closed — there is no day to apply
 * for. Not the same as `listingAvailable`, which is only "free right now".
 */
export function canApplyToRoom(room: RoomAvailabilityInput): boolean {
  const { listingAvailable, nextAvailableDate } = getListingAvailability(room);
  return listingAvailable || nextAvailableDate !== null;
}

/**
 * What the tenant dashboard's "Available rooms" (referrals) may list: a room
 * that is free TODAY, or one with an admin-CONFIRMED move-out date — which is
 * the only way `available_from` ever gets a value (see the module comment).
 * An occupied room with no confirmed date is not referrable: there is nothing
 * to promise a friend.
 *
 * Deliberately separate from getListingAvailability: the public grid hides a
 * room until its date arrives, but a tenant referring a friend wants to know
 * about the room that frees up next month — that is the referral.
 */
export function getReferralAvailability(room: RoomAvailabilityInput): {
  referrable: boolean;
  /** Set only when the room is NOT free today but has a confirmed date. */
  availableFrom: string | null;
} {
  const listing = getListingAvailability(room);
  if (listing.listingAvailable)
    return { referrable: true, availableFrom: null };
  if (listing.nextAvailableDate) {
    return { referrable: true, availableFrom: listing.nextAvailableDate };
  }
  return { referrable: false, availableFrom: null };
}

export function formatNextAvailableDate(
  iso: string,
  language: "en" | "de"
): string {
  const d = parseDateOnly(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Move-in filter: user wants `moveInDate` (yyyy-mm-dd from input); room is ok if free on or before that day. */
export function moveInAllowsBooking(
  moveInYyyyMmDd: string,
  listingAvailable: boolean,
  nextAvailableDate: string | null
): boolean {
  if (listingAvailable) return true;
  if (!nextAvailableDate) return false;
  const want = parseDateOnly(moveInYyyyMmDd);
  const from = parseDateOnly(nextAvailableDate);
  if (Number.isNaN(want.getTime()) || Number.isNaN(from.getTime()))
    return false;
  return want >= from;
}
